// src/components/ChatScreen.tsx
import React, { useRef, useState, useEffect } from "react";
import {
  ArrowLeft,
  Plus,
  Search,
  Send,
  Users,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { ScrollArea } from "../ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";

import { useCometChat } from "../../lib/hooks/useCometChat";
import { CometChat } from "@cometchat/chat-sdk-javascript";

const getInitials = (name = "U") =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

const formatTime = (ts?: number) =>
  ts ? new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

const MessageStatus = ({ msg, own }: any) => {
  if (!own) return null;

  const read = msg.readAt;
  const delivered = msg.deliveredAt;

  if (read) return <span className="ml-1 text-blue-500 text-xs">✓✓</span>;
  if (delivered) return <span className="ml-1 text-gray-600 text-xs">✓✓</span>;
  return <span className="ml-1 text-gray-500 text-xs">✓</span>;
};

export const ChatScreen = ({ userRole, onBack, chatUser }: { userRole:string, onBack?: () => void, chatUser:any }) => {
  const {
    user,
    users,
    conversations,
    messages,
    presence,
    typingUsers,
    activeConversation,
    openConversation,
    sendMessage,
    loadMoreMessages,
  } = useCometChat();

  const [newMessage, setNewMessage] = useState("");
  const [searchQuery] = useState("");
  const [isNewChatOpen, setIsNewChatOpen] = useState(false);
  const [isGroupChatOpen, setIsGroupChatOpen] = useState(false);
  const [groupName, setGroupName] = useState("");

  // Scroll reference
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const prevHeightRef = useRef(0);

  // Auto scroll bottom when conversation or messages change (only if it's a new message)
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [activeConversation, messages]);

  // Handle loading older messages & retain scroll position
  const handleScroll = async () => {
    const el = scrollRef.current;
    if (!el) return;

    if (el.scrollTop === 0) {
      prevHeightRef.current = el.scrollHeight;
      await loadMoreMessages();

      // stabilize scroll so it doesn't jump
      setTimeout(() => {
        const newHeight = el.scrollHeight;
        el.scrollTop = newHeight - prevHeightRef.current;
      }, 30);
    }
  };

  const formatDateHeader = (ts: number) => {
    const d = new Date(ts);
    const today = new Date();

    if (
      d.getDate() === today.getDate() &&
      d.getMonth() === today.getMonth() &&
      d.getFullYear() === today.getFullYear()
    ) {
      return "Today";
    }

    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (
      d.getDate() === yesterday.getDate() &&
      d.getMonth() === yesterday.getMonth() &&
      d.getFullYear() === yesterday.getFullYear()
    ) {
      return "Yesterday";
    }

    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    await sendMessage(newMessage.trim());
    setNewMessage("");
  };

  const filteredConversations = conversations.filter((c) =>
    c?.conversationWith?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const other = activeConversation?.conversationWith;
  const otherUid = other?.uid || other?.guid;
  const isTyping = otherUid && typingUsers[otherUid];
  const presenceInfo = otherUid ? presence[otherUid] : null;

  return (
    <>
      <div className="flex h-screen overflow-hidden bg-gray-50">
        {/* LEFT SIDEBAR */}
        <div className={`w-full md:w-80 lg:w-96 bg-white border-r flex-col md:flex ${activeConversation ? "hidden md:flex" : "flex"}`}>
          <div className="p-4 border-b flex justify-between">
            <div className="flex items-center gap-2">
              {onBack && (
                <Button variant="ghost" size="sm" onClick={onBack}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <h2 className="text-[#2762AC] font-semibold">Messages</h2>
            </div>

            <div className="flex gap-2">
              <Button size="sm" className="bg-[#2762AC]" onClick={() => setIsNewChatOpen(true)}>
                <Plus className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsGroupChatOpen(true)}>
                <Users className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <ScrollArea className="flex-1">
            {filteredConversations.map((c) => {
              const u = c.conversationWith;
              const pres = presence[u.uid];
              return (
                <div
                  key={c.conversationId}
                  onClick={() => openConversation(c)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 flex gap-3 ${
                    activeConversation?.conversationId === c.conversationId ? "bg-blue-50" : ""
                  }`}
                >
                  <div className="relative">
                    <Avatar>
                      <AvatarFallback>{getInitials(u.name)}</AvatarFallback>
                    </Avatar>
                    {pres?.online ? (
                      <span className="absolute -right-1 -bottom-1 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
                    ) : (
                      <span className="absolute -right-1 -bottom-1 w-3 h-3 bg-gray-400 border-2 border-white rounded-full" />
                    )}
                  </div>
                  <div>
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs text-gray-600">
                      {c.lastMessage?.text || ""}
                    </div>
                  </div>
                </div>
              );
            })}
          </ScrollArea>
        </div>

        {/* RIGHT CHAT WINDOW */}
        <div className={`flex-1 flex-col md:flex ${!activeConversation ? "hidden" : ""}`}>
          {activeConversation ? (
            <>
              {/* HEADER */}
              <div className="p-4 border-b bg-white flex items-center gap-3">
                <Button className="md:hidden" variant="ghost" size="sm" onClick={() => openConversation(null)}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>

                <div className="relative">
                  <Avatar>
                    <AvatarFallback>{getInitials(other?.name)}</AvatarFallback>
                  </Avatar>
                  {presenceInfo?.online ? (
                    <span className="absolute -right-1 -bottom-1 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
                  ) : (
                    <span className="absolute -right-1 -bottom-1 w-3 h-3 bg-gray-400 border-2 border-white rounded-full" />
                  )}
                </div>

                <div>
                  <div className="font-semibold">{other?.name}</div>
                  <div className="text-gray-600 text-sm">
                    {isTyping
                      ? "typing…"
                      : presenceInfo?.online
                      ? "Online"
                      : presenceInfo?.lastSeen
                      ? `Last seen ${new Date(presenceInfo.lastSeen).toLocaleString()}`
                      : "Offline"}
                  </div>
                </div>
              </div>

              {/* CHAT MESSAGES */}
              <div
                className="flex-1 overflow-y-auto p-4"
                ref={scrollRef}
                onScroll={handleScroll}
              >
                {messages.map((msg, i) => {
                  const msgTime = msg.sentAt*1000 || msg._cometChatTimestamp*1000;

                  const showDateHeader =
                    i === 0 ||
                    formatDateHeader(messages[i - 1].sentAt*1000) !==
                      formatDateHeader(msgTime);

                  const own = msg.sender?.uid === user?.uid;

                  return (
                    <React.Fragment key={msg.id}>
                      {showDateHeader && (
                        <div className="text-center text-gray-500 text-sm my-3">
                          {formatDateHeader(msgTime)}
                        </div>
                      )}

                      <div className={`flex my-1 ${own ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[75%] px-4 py-2 rounded-2xl ${
                            own ? "bg-[#2762AC] text-white" : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          <div>{msg.text}</div>

                          <div className="text-xs opacity-80 text-right mt-1 flex items-center justify-end gap-1">
                            {new Date(msgTime).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}

              </div>

              {/* INPUT */}
              <div className="p-4 border-t bg-white flex gap-2">
                <Textarea
                  rows={1}
                  className="resize-none flex-1"
                  placeholder="Type message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                />
                <Button onClick={handleSend} className="bg-[#2762AC]">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center flex-1 text-gray-500">Select a conversation</div>
          )}
        </div>
      </div>

      {/* NEW CHAT MODAL */}
      <Dialog open={isNewChatOpen} onOpenChange={setIsNewChatOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Start New Chat</DialogTitle>
            <DialogDescription>Select a user</DialogDescription>
          </DialogHeader>

          <div className="h-80 overflow-y-auto">
            {users.map((u) => (
              <div
                key={u.uid}
                className="p-3 flex gap-3 hover:bg-gray-50 cursor-pointer"
                onClick={() => {
                  openConversation({
                    conversationId: `user_${user.uid}_${u.uid}`,
                    conversationType: "user",
                    conversationWith: u,
                  });
                  setIsNewChatOpen(false);
                }}
              >
                <Avatar>
                  <AvatarFallback>{getInitials(u.name)}</AvatarFallback>
                </Avatar>
                <div className="font-medium">{u.name}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* GROUP MODAL */}
      <Dialog open={isGroupChatOpen} onOpenChange={setIsGroupChatOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Group</DialogTitle>
            <DialogDescription>Enter group name</DialogDescription>
          </DialogHeader>

          <Input
            placeholder="Group name"
            className="mt-2"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
          />

          <DialogFooter>
            <Button
              className="bg-[#2762AC]"
              disabled={!groupName.trim()}
              onClick={async () => {
                const gid = "grp_" + Date.now();
                const group = await CometChat.createGroup(
                  new CometChat.Group(gid, groupName, CometChat.GROUP_TYPE.PUBLIC)
                );
                await CometChat.joinGroup(gid, CometChat.GROUP_TYPE.PUBLIC, "");

                openConversation({
                  conversationId: "cnv_" + Date.now(),
                  conversationType: "group",
                  conversationWith: group,
                });

                setGroupName("");
                setIsGroupChatOpen(false);
              }}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
